/* eslint-disable @typescript-eslint/no-explicit-any */
// react-i18next + TS 4.9.5 workaround — see src/i18n/index.ts.
import React, { useState, useCallback } from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { ProfileType } from '../../types/enums';
import { AvailableProfileDto } from '../../services/api';
import ElevationModal from '../ElevationModal/ElevationModal';
import styles from './LoginChooser.module.css';

const useTranslation = useTranslationUntyped as any;

interface LoginChooserProps {
    /**
     * Profiles returned by the v2 login response for a user who has no
     * LastActiveProfileId. Filtered per PO-D-002 (Customer is mobile-only
     * until Q3 2026) before render.
     */
    profiles: AvailableProfileDto[];
    /**
     * Invoked when the user aborts the chooser (e.g., wants to sign out
     * and try a different account). Parent is expected to clear tokens.
     */
    onCancel: () => void;
}

/**
 * Auth v2 post-login chooser modal (AB#1434.b).
 *
 * Rendered on `/form/login` only when `loginV2` returns an identity-only
 * token (`activeProfileId == null`) AND the user owns ≥ 2 surface-able
 * profiles. Selecting a profile calls `switchProfileV2`, which mints a
 * scoped token, resets client caches, and navigates home.
 *
 * SuperAdmin picks open the ElevationModal first (AB#1437). After the
 * user re-enters their password the switch is retried automatically.
 *
 * Kept intentionally dependency-light so the component can render before
 * MainLayout mounts — it lives adjacent to LoginForm and is styled as a
 * modal on top of the login page so there's no "logged-in-looking" flash.
 */
const LoginChooser: React.FC<LoginChooserProps> = ({ profiles, onCancel }) => {
    const { t } = useTranslation('auth');
    const { switchProfileV2, loading } = useAuth();
    const [pendingElevationTarget, setPendingElevationTarget] = useState<number | null>(null);

    // The backend serializes ProfileType as a string in JSON
    // (`"SalonOwner"`, `"SuperAdmin"`, …) but the generated TS type is a
    // numeric enum. Normalize both shapes so downstream comparisons and
    // translations work regardless of the wire format.
    const normalized = profiles.map(p => ({ ...p, type: normalizeProfileType(p.type) }));

    // PO-D-002: Customer role is mobile-only on web until Q3 2026.
    const surfaceable = normalized.filter(p => p.type !== ProfileType.Customer);

    const handleSelect = useCallback(async (profile: AvailableProfileDto) => {
        if (loading) return;
        const result = await switchProfileV2(profile.id);
        if (result && 'needsElevation' in result) {
            setPendingElevationTarget(profile.id);
        }
        // On non-elevation success, useAuth.switchProfileV2 navigates home.
    }, [loading, switchProfileV2]);

    const handleElevationSuccess = useCallback(async () => {
        const target = pendingElevationTarget;
        setPendingElevationTarget(null);
        if (target == null) return;
        await switchProfileV2(target);
    }, [pendingElevationTarget, switchProfileV2]);

    if (surfaceable.length === 0) {
        // Degenerate case: all profiles are Customer (mobile-only — PO-D-002).
        // Calling onCancel clears the identity-only token so the user returns to
        // a clean login state instead of being silently stuck with a stale token.
        onCancel();
        return null;
    }

    return (
        <div className={styles.backdrop} role="dialog" aria-modal="true" aria-labelledby="login-chooser-title">
            <div className={styles.card} aria-label={t('chooser.ariaLabel')}>
                <h2 id="login-chooser-title" className={styles.title}>{t('chooser.title')}</h2>
                <p className={styles.subtitle}>{t('chooser.subtitle')}</p>

                <ul className={styles.list}>
                    {surfaceable.map(profile => {
                        const isSuperAdmin = profile.type === ProfileType.SuperAdmin;
                        return (
                            <li
                                key={profile.id}
                                className={styles.row}
                                role="button"
                                tabIndex={0}
                                aria-disabled={loading}
                                onClick={() => handleSelect(profile)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleSelect(profile);
                                    }
                                }}
                            >
                                <div className={styles.roleCol}>
                                    <span className={styles.roleLabel}>{translatedRole(t, profile.type)}</span>
                                    {profile.businessName && (
                                        <span className={styles.businessLabel}>{profile.businessName}</span>
                                    )}
                                    {isSuperAdmin && (
                                        <span className={styles.elevationNote}>{t('chooser.elevationNote')}</span>
                                    )}
                                </div>
                                <span className={styles.chevron} aria-hidden="true">›</span>
                            </li>
                        );
                    })}
                </ul>

                {loading && <div className={styles.inFlight}>{t('switcher.inFlight')}</div>}

                <div className={styles.footer}>
                    <button type="button" className={styles.signOutLink} onClick={onCancel} disabled={loading}>
                        {t('chooser.signOutAndRetry')}
                    </button>
                </div>
            </div>

            <ElevationModal
                open={pendingElevationTarget != null}
                onSuccess={handleElevationSuccess}
                onCancel={() => setPendingElevationTarget(null)}
            />
        </div>
    );
};

function translatedRole(t: any, type: ProfileType): string {
    switch (type) {
        case ProfileType.SalonOwner:
            return t('roles.salonOwner');
        case ProfileType.SalonStaff:
            return t('roles.salonStaff');
        case ProfileType.SuperAdmin:
            return t('roles.superAdmin');
        case ProfileType.Customer:
            return t('roles.customer');
        default:
            return String(type);
    }
}

/**
 * Accepts either the numeric ProfileType enum value or its string name
 * (`"SalonOwner"`, `"SalonStaff"`, `"SuperAdmin"`, `"Customer"`) and
 * returns the enum value. Unknown input falls back to Customer, matching
 * the ProfileSwitcher's conservative default.
 */
function normalizeProfileType(raw: unknown): ProfileType {
    if (typeof raw === 'number') return raw as ProfileType;
    if (typeof raw === 'string') {
        switch (raw) {
            case 'Customer': return ProfileType.Customer;
            case 'SalonOwner': return ProfileType.SalonOwner;
            case 'SalonStaff': return ProfileType.SalonStaff;
            case 'SuperAdmin': return ProfileType.SuperAdmin;
            default: return ProfileType.Customer;
        }
    }
    return ProfileType.Customer;
}

export default LoginChooser;
